import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, MessageCircle, Send, X } from 'lucide-react';

const initialChatForm = {
  name: '',
  email: '',
  company: '',
  message: ''
};

export default function LiveChatWidget({ apiBaseUrl }) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(initialChatForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      await axios.post(`${apiBaseUrl}/chat`, formData);
      setStatus('success');
      setFormData(initialChatForm);
    } catch (error) {
      setStatus('error');
      console.error('Error submitting live chat inquiry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="live-chat-widget" data-testid="live-chat-widget">
      {isOpen && (
        <div className="live-chat-panel" data-testid="live-chat-panel">
          <div className="live-chat-header">
            <div>
              <p className="live-chat-kicker" data-testid="live-chat-kicker">ZenomTech Live Chat</p>
              <h3 data-testid="live-chat-title">Talk to a BSS/OSS expert</h3>
            </div>
            <button
              type="button"
              className="live-chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close live chat"
              data-testid="live-chat-close-button"
            >
              <X size={18} />
            </button>
          </div>

          <div className="live-chat-body">
            <div className="live-chat-message agent" data-testid="live-chat-greeting-message">
              Hello — share your question and we’ll route it to the right ZenomTech specialist.
            </div>

            <form onSubmit={handleSubmit} className="live-chat-form" data-testid="live-chat-form">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name *"
                required
                className="live-chat-input"
                data-testid="live-chat-name-input"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email *"
                required
                className="live-chat-input"
                data-testid="live-chat-email-input"
              />
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Company"
                className="live-chat-input"
                data-testid="live-chat-company-input"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="How can we help? *"
                required
                rows={3}
                className="live-chat-input live-chat-textarea"
                data-testid="live-chat-message-input"
              />
              <button
                type="submit"
                className="live-chat-submit"
                disabled={isSubmitting}
                data-testid="live-chat-submit-button"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send size={16} /> Send
                  </>
                )}
              </button>
            </form>

            {status === 'success' && (
              <div className="live-chat-status success" data-testid="live-chat-success-message">
                <CheckCircle size={16} /> Message received. We’ll get back to you soon.
              </div>
            )}
            {status === 'error' && (
              <div className="live-chat-status error" data-testid="live-chat-error-message">
                Message could not be sent. Please try the contact form.
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className="live-chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close live chat' : 'Open live chat'}
        data-testid="live-chat-toggle-button"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        <span>{isOpen ? 'Close' : 'Chat'}</span>
      </button>
    </div>
  );
}